import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material";

// `Icons[iconName]` is a computed lookup, so webpack cannot tree-shake the
// @mui/icons-material barrel and retains all ~10,600 icons. Statically
// importing it here put 3.75 MB into main.js (59% of it), because DynamicIcon
// is reached from core components like NiceService and NiceSearchResult.
//
// Loading the barrel through a dynamic import keeps it out of main.js: it
// becomes a single chunk, fetched the first time a dynamic icon actually
// renders and reused for every icon after that.
let iconsModule = null;
let iconsPromise = null;

const loadIcons = () => {
  if (iconsModule) {
    return Promise.resolve(iconsModule);
  }
  if (!iconsPromise) {
    iconsPromise = import("@mui/icons-material")
      .then((mod) => {
        iconsModule = mod;
        return mod;
      })
      .catch((err) => {
        // Allow a later render to retry instead of caching the rejection.
        iconsPromise = null;
        throw err;
      });
  }
  return iconsPromise;
};

export const DynamicIcon = ({ iconName }) => {
  const theme = useTheme();
  const [icons, setIcons] = useState(iconsModule);

  useEffect(() => {
    if (icons) {
      return undefined;
    }

    let active = true;
    loadIcons()
      .then((mod) => {
        if (active) {
          setIcons(mod);
        }
      })
      .catch((err) => {
        console.error("Failed to load icon set:", err);
      });

    return () => {
      active = false;
    };
  }, [icons]);

  // Renders nothing until the icon set arrives, matching the existing
  // behaviour for an unknown icon name.
  if (!icons) {
    return null;
  }

  const IconComponent = icons[iconName];

  if (!IconComponent) {
    return null;
  }

  return (
    <IconComponent
      sx={{ color: theme.palette.primary.main, fontSize: "24px" }}
    />
  );
};
